import React, { useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';

export interface SwipeCardRef {
  swipe: (dir: string) => Promise<void>;
  restoreCard: () => Promise<void>;
}

interface SwipeCardProps {
  onSwipe?: (direction: string) => void;
  preventSwipe?: string[];
  className?: string;
  children: React.ReactNode;
}

const SwipeCard = forwardRef<SwipeCardRef, SwipeCardProps>(
  ({ onSwipe, preventSwipe = [], className = '', children }, ref) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [gone, setGone] = useState(false);
    const startPos = useRef({ x: 0, y: 0 });

    const SWIPE_THRESHOLD = 100;
    const FLY_DISTANCE = 1000;
    const INDICATOR_THRESHOLD = 20;

    const reset = useCallback(() => {
      setOffset({ x: 0, y: 0 });
      setGone(false);
    }, []);

    const flyOut = useCallback(
      (dir: string) => {
        const xOut = dir === 'left' ? -FLY_DISTANCE : dir === 'right' ? FLY_DISTANCE : 0;
        const yOut = dir === 'up' ? -FLY_DISTANCE : dir === 'down' ? FLY_DISTANCE : 0;
        setOffset({ x: xOut, y: yOut });
        setGone(true);

        if (navigator.vibrate) {
          navigator.vibrate(30);
        }

        onSwipe?.(dir);
      },
      [onSwipe],
    );

    useImperativeHandle(ref, () => ({
      swipe: async (dir: string) => flyOut(dir),
      restoreCard: async () => reset(),
    }));

    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        if (gone) return;
        setDragging(true);
        startPos.current = { x: e.clientX, y: e.clientY };
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      },
      [gone],
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!dragging) return;
        let dx = e.clientX - startPos.current.x;
        let dy = e.clientY - startPos.current.y;
        if (preventSwipe.includes('left') && dx < 0) dx = 0;
        if (preventSwipe.includes('right') && dx > 0) dx = 0;
        if (preventSwipe.includes('up') && dy < 0) dy = 0;
        if (preventSwipe.includes('down') && dy > 0) dy = 0;
        setOffset({ x: dx, y: dy });
      },
      [dragging, preventSwipe],
    );

    const handlePointerUp = useCallback(() => {
      if (!dragging) return;
      setDragging(false);

      const absX = Math.abs(offset.x);
      const absY = Math.abs(offset.y);

      if (absX > SWIPE_THRESHOLD && absX >= absY) {
        flyOut(offset.x > 0 ? 'right' : 'left');
      } else if (absY > SWIPE_THRESHOLD && absY > absX) {
        flyOut(offset.y > 0 ? 'down' : 'up');
      } else {
        setOffset({ x: 0, y: 0 });
      }
    }, [dragging, offset, flyOut]);

    const rotation = offset.x / 20;
    const opacity = gone ? 0 : 1;

    // Интенсивность обводки: 0..1
    const likeIntensity = Math.min(Math.max((offset.x - INDICATOR_THRESHOLD) / (SWIPE_THRESHOLD - INDICATOR_THRESHOLD), 0), 1);
    const nopeIntensity = Math.min(Math.max((-offset.x - INDICATOR_THRESHOLD) / (SWIPE_THRESHOLD - INDICATOR_THRESHOLD), 0), 1);

    // Цвет обводки
    let borderColor = 'transparent';
    if (likeIntensity > 0) {
      borderColor = `rgba(34, 197, 94, ${likeIntensity * 0.7})`; // green-500
    } else if (nopeIntensity > 0) {
      borderColor = `rgba(239, 68, 68, ${nopeIntensity * 0.7})`; // red-500
    }

    return (
      <div
        ref={cardRef}
        className={className}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg)`,
          opacity,
          transition: dragging ? 'none' : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease',
          touchAction: 'none',
          userSelect: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
          borderRadius: '1rem',
          border: `3px solid ${borderColor}`,
        }}
      >
        {children}
      </div>
    );
  },
);

SwipeCard.displayName = 'SwipeCard';

export default SwipeCard;
